import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Invoice } from '@/types'
import { Eye, FilePdf } from 'phosphor-react'
import {
  formatCurrency,
  formatInvoiceDate,
  formatInvoicePeriod,
  getBilledEntityLabel,
  getBilledEntityTypeLabel,
} from '@/utils/facturation-utils'

interface FacturationColumnDefsParams {
  handleViewDetail: (invoice: Invoice) => void
  showEntityColumns?: boolean
}

export const getFacturationColumnDefs = ({
  handleViewDetail,
  showEntityColumns = false,
}: FacturationColumnDefsParams): ColDef[] => {
  const columns: ColDef[] = [
    {
      field: 'period',
      headerName: 'Période',
      flex: 1,
      minWidth: 150,
      valueGetter: params => {
        const invoice = params.data as Invoice | undefined
        if (!invoice) return ''
        return formatInvoicePeriod(invoice.periodMonth, invoice.periodYear)
      },
      comparator: (_a, _b, nodeA, nodeB) => {
        const invoiceA = nodeA.data as Invoice
        const invoiceB = nodeB.data as Invoice
        const dateA = invoiceA.periodYear * 100 + invoiceA.periodMonth
        const dateB = invoiceB.periodYear * 100 + invoiceB.periodMonth
        return dateA - dateB
      },
    },
    ...(showEntityColumns
      ? [
          {
            field: 'billedEntity.companyName',
            headerName: 'Entité facturée',
            flex: 1,
            minWidth: 180,
            valueGetter: params => {
              const invoice = params.data as Invoice | undefined
              if (!invoice?.billedEntity) return '-'
              return getBilledEntityLabel(invoice.billedEntity)
            },
          } as ColDef,
          {
            field: 'billedEntity.type',
            headerName: 'Type',
            flex: 1,
            minWidth: 140,
            valueGetter: params => {
              const invoice = params.data as Invoice | undefined
              if (!invoice?.billedEntity) return '-'
              return getBilledEntityTypeLabel(invoice.billedEntity.type)
            },
          } as ColDef,
        ]
      : []),
    {
      field: 'licenseCount',
      headerName: 'Licences',
      flex: 0.6,
      minWidth: 100,
      type: 'numericColumn',
    },
    {
      field: 'totalHt',
      headerName: 'Total HT',
      flex: 1,
      minWidth: 120,
      type: 'numericColumn',
      valueFormatter: params => formatCurrency(params.value ?? 0),
    },
    {
      field: 'totalVat',
      headerName: 'TVA',
      flex: 1,
      minWidth: 110,
      type: 'numericColumn',
      valueFormatter: params => formatCurrency(params.value ?? 0),
    },
    {
      field: 'totalTtc',
      headerName: 'Total TTC',
      flex: 1,
      minWidth: 120,
      type: 'numericColumn',
      valueFormatter: params => formatCurrency(params.value ?? 0),
    },
    {
      field: 'generatedAt',
      headerName: 'Date émission',
      flex: 1,
      minWidth: 140,
      valueFormatter: params => (params.value ? formatInvoiceDate(params.value) : '-'),
    },
    {
      field: 'pdfUrl',
      headerName: 'PDF',
      flex: 0.6,
      minWidth: 90,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Invoice>) => {
        if (!params.data?.pdfUrl) return '-'
        return (
          <a
            href={params.data.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            onClick={event => event.stopPropagation()}
          >
            <FilePdf className="w-4 h-4" weight="regular" />
            PDF
          </a>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Invoice>) => {
        if (!params.data) return null
        return (
          <button
            type="button"
            onClick={() => handleViewDetail(params.data!)}
            className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:text-[var(--color-primary)]/80 transition-colors"
            title="Voir le détail"
          >
            <Eye className="w-4 h-4" weight="regular" />
            Détail
          </button>
        )
      },
    },
  ]

  return columns
}
